'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { SocketState } from '@/types/socket.types';

/**
 * Internal generic hook to manage a single WebSocket connection
 * @param url Base URL (e.g., wss://...)
 * @param endpoint Specific endpoint (e.g., /ws/market)
 */
export const useWebSocketConnection = (
  url: string | undefined,
  endpoint: string,
  connectImmediately: boolean = true
): SocketState => {
  const [isConnected, setIsConnected] = useState(false);
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const connectRef = useRef<() => void>(() => {});

  const startPing = useCallback(() => {
    if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
    pingIntervalRef.current = setInterval(() => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.send('PING');
      }
    }, 5000);
  }, []);

  const stopPing = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!url) return;

    const fullUrl = `${url}${endpoint}`;

    try {
      if (socketRef.current) {
        socketRef.current.onclose = null;
        socketRef.current.close();
      }

      const ws = new WebSocket(fullUrl);
      socketRef.current = ws;
      setSocket(ws);

      ws.onopen = () => {
        setIsConnected(true);
        startPing();
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        setSocket(null);
        socketRef.current = null;
        stopPing();
        reconnectTimeoutRef.current = setTimeout(() => connectRef.current(), 3000);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {
      reconnectTimeoutRef.current = setTimeout(() => connectRef.current(), 3000);
    }
  }, [url, endpoint, startPing, stopPing]);

  useEffect(() => {
    connectRef.current = connect;
    if (connectImmediately && url) {
      Promise.resolve().then(() => connect());
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.onclose = null;
        socketRef.current.close();
        socketRef.current = null;
        setSocket(null);
      }
      stopPing();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connect]);

  return { socket, isConnected };
};
